import * as React from 'react';

import { translate } from '~l10n/client';

import { IconButton } from '~shared/components/buttons';
import { Dialog, DialogProps } from '~shared/components/Dialog';
import {
  IconCheckmark,
  IconTrash,
  IconX,
} from '~shared/components/icons/standard';

export interface Props extends Pick<DialogProps, 'open' | 'onClose'> {
  children: React.ReactNode;
  /** Is the popover being opened for the first time or is there already a value? */
  hasExistingValue?: boolean;
  /** Is the save button disabled */
  isSaveDisabled?: boolean;
  isErrorState?: boolean;
  onSaveValue: () => void;
  onRemoveValue: () => void;
  title: string;
}
export const QuillPopover = ({
  children,
  open,
  onClose,
  hasExistingValue,
  onSaveValue,
  onRemoveValue,
  isSaveDisabled = false,
  isErrorState = false,
  title,
}: Props) => {
  const handleSavePress = () => {
    onSaveValue();
    onClose(false);
  };

  const handleClearPress = () => {
    onRemoveValue();
    onClose(false);
  };

  return (
    <Dialog
      id="quill-popover"
      padding="none"
      /**
       * Hiding overflow here prevents scrollbars when focusing on the MathLive
       * input. Bottom offset fixes an issue with MathLive's on-screen keyboard.
       */
      __customClassEscapeHatch="overflow-x-hidden overflow-y-hidden bottom-24"
      title={title}
      open={open}
      onClose={onClose}
      closeLabel={translate('Close')}
      hasCustomCloseButton
    >
      {(headerProps) => (
        <div className="p-6">
          <header className="flex items-start justify-between -mt-2">
            <h3 className="m-0 pt-2">
              <span
                className="font-display text-md font-500 leading-8 text-blue-900 outline-none"
                ref={headerProps.headingRef}
                tabIndex={-1}
              >
                {headerProps.title}
              </span>
            </h3>
            <div className="flex-none flex -mr-2">
              {hasExistingValue && !isErrorState && (
                <div className="pl-4">
                  <IconButton onClick={handleClearPress} isRisky>
                    <IconTrash />
                  </IconButton>
                </div>
              )}
              {!isErrorState && (
                <div className="pl-2">
                  <IconButton
                    onClick={handleSavePress}
                    disabled={isSaveDisabled}
                  >
                    <IconCheckmark />
                  </IconButton>
                </div>
              )}
              <div className="pl-2">
                <IconButton appearance="ghost" onClick={() => onClose(false)}>
                  <IconX />
                </IconButton>
              </div>
            </div>
          </header>
          <div className="flex pt-4">
            <div className="flex-1 min-w-0">{children}</div>
          </div>
        </div>
      )}
    </Dialog>
  );
};
